import { Injectable } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { first, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { HeadersService } from '../headers/headers.service';
import { DbPagination } from '../../../../models/pagination';
import { firstValueFrom, Observable, of } from 'rxjs';
import { MatchStem } from '../../../../models/match-stem';
import { DigestResponse, DigestSearchResult } from '../../../../models/digest';

interface ContextDataItem {
  docs: Array<{
    excerpt: string;
    id: string;
  }>;
  id: number;
}

interface ResponseFromApi {
  data: Array<ContextDataItem>;
  pagination: DbPagination;
}

@Injectable({
  providedIn: 'root'
})
export class ApiTerritoryWatchSendingService {

  constructor(private http: HttpClient,
              private headersService: HeadersService) { }

  retrieveContextFromCollectiveOrder(collectiveOrderId: number): Observable<ContextDataItem> {
    const body = {
      'fields': [
        'id',
        'docs'
      ],
      'filters': {
        'id': collectiveOrderId
      },
      'limit': 1,
      'offset': 0
    };
    return this.http.post<any>(`${environment.explainApiUrl}collective_order`, body, this.headersService.httpHeaders)
      .pipe(map((response: ResponseFromApi) => {
        return response.data[0];
      }));
  }

  retrieveDigestResultsFromS3(
    digestResultsFileUrl: string
  ): Promise<DigestResponse | null> {
    return firstValueFrom(this.http
      .get<DigestResponse>(digestResultsFileUrl, {
        responseType: 'json',
      })
      .pipe(first()));
  }

  retrieveDocumentMatchStemsFromS3(
    s3DeduplicatedDigestResultsFileUrl: string | null | undefined,
    documentId: string
  ): Observable<MatchStem[] | null> {
    return this.retrieveDocumentMatchFromS3(
      s3DeduplicatedDigestResultsFileUrl,
      documentId
    ).pipe(map((result) => result?.match_stems || null));
  }

  /** Method getting digestResults file and extracting admin doc matchStems */
  retrieveDocumentMatchFromS3(
    s3DeduplicatedDigestResultsFileUrl: string | null | undefined,
    documentId: string
  ): Observable<DigestSearchResult | null> {
    if (!s3DeduplicatedDigestResultsFileUrl) {
      return of(null);
    }

    return this.http
      .get<DigestResponse>(s3DeduplicatedDigestResultsFileUrl, {
        responseType: 'json',
      })
      .pipe(
        map((response) => {
          // Remove the filter to use ALL content types
          const allContentResults = response.digests_by_content_type;

          for (const entry of allContentResults) {
            // Skip entries that don't have search_results
            if (!entry.search_results) {
              continue;
            }

            const results = Array.isArray(entry.search_results)
              ? entry.search_results
              : entry.search_results.data;

            const match = results.find((item) => item.id === documentId);
            if (match) {
              return match;
            }
          }

          return null;
        })
      );
  }

  /** Method to retrieve S3DeduplicatedDigestResults file url from a collectiveOrderId */
  retrieveS3DeduplicatedDigestResultsFileUrlFromCollectiveOrderId(collectiveOrderId: number): Observable<string> {
    const baseUrl = environment.s3DigestResultsBaseUrl;
    return this.http
      .get<{ id: number, digest_id: number, s3_file_url: string }>(`${environment.explainApiUrl}collective_order/${collectiveOrderId}`, this.headersService.httpHeaders)
      .pipe(first())
      .pipe(map((response: { id: number, digest_id: number, s3_file_url: string }) => {
        const fileName = response.s3_file_url.split('/').slice(3).join('/');
        return `${baseUrl}/${fileName}`;
      }));
  }
}
